"use client";

import React, { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Standalone Overlay - positions content relative to a target element.
 *
 * @param {HTMLElement|{current:HTMLElement}} target - DOM element or ref
 * @param {boolean} show - Whether overlay is visible
 * @param {string} placement - 'top' | 'bottom' | 'left' | 'right'
 */
export function Overlay({
  children,
  target,
  show = false,
  placement = "top",
  ...props
}) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!show) {
      setRect(null);
      return;
    }
    const el = target?.current ?? target;
    if (!el || typeof el.getBoundingClientRect !== "function") return;
    setRect(el.getBoundingClientRect());
  }, [show, target]);

  if (!show || !rect || typeof document === "undefined") return null;

  const wrapperStyle = {
    position: "fixed",
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    pointerEvents: "none",
    zIndex: 9999,
  };

  const content =
    typeof children === "function" ? children({ show, placement }) : children;

  return createPortal(
    <div style={wrapperStyle} {...props}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {content}
      </div>
    </div>,
    document.body
  );
}
